<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuery } from '@tanstack/vue-query';
import { monthlyTrackingApi, type MonthlyTrackingRow } from '@/api/planning/monthly-tracking';
import { MONTH_NAMES_ES } from '@/utils/monthly-expectation';
import { careerMetadata } from '@/utils/career-metadata';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { useMobile } from '@/composables/useMobile';
import { usePermissions } from '@/composables/usePermissions';
import { useGroupFilterStore } from '@/stores/group-filter';
import { useServiceCategoryFilterStore } from '@/stores/service-category-filter';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import ServiceCategoryFilterSelect from '@/components/service-category-filter-select.vue';
import {
  useMonthlyTrackingStore, type MonthlyTrackingStatusFilter,
} from '@/stores/monthly-tracking';
import {
  applySearch, applyStatus, applyCareer, groupByPatient, patientRequirementsPath,
  type GroupedPatientRow,
} from './grouping';
import MonthlyTrackingCard from './monthly-tracking-card.vue';

// The name opens the patient's requirements; without access to patients it stays plain text.
const { hasPermission } = usePermissions();
const canOpenPatient = computed(() => hasPermission.value('patients', 'show'));

const PERCENT_MULTIPLIER = 100;
const YEAR_RANGE = 1;
const WARNING_THRESHOLD = 0.8;
const OVER_THRESHOLD = 1.0;
const HIGH_DRIFT = 3;

interface CareerOption {
  value: string;
  title: string;
  icon: string;
  color: string;
}

const STATUS_OPTIONS: Array<{ value: MonthlyTrackingStatusFilter;
  title: string;
  icon: string; }> = [
    { value: 'all', title: 'Todos', icon: 'mdi-format-list-bulleted' },
    { value: 'drift', title: 'Por revisar', icon: 'mdi-alert-circle-outline' },
    { value: 'unmarked', title: 'Sin realizar', icon: 'mdi-clock-alert-outline' },
    { value: 'over_budget', title: 'Sobre tope', icon: 'mdi-arrow-up-bold' },
  ];

const MONTH_OPTIONS = MONTH_NAMES_ES.map((name, i) => ({
  value: i + 1,
  title: name.charAt(0).toUpperCase() + name.slice(1),
}));
const now = new Date();
const YEAR_OPTIONS = [
  now.getFullYear() - YEAR_RANGE, now.getFullYear(), now.getFullYear() + YEAR_RANGE,
];

const selectedYear = ref(now.getFullYear());
const selectedMonth = ref(now.getMonth() + 1);
// VDataTable types expanded keys as strings even though item-value yields
// patientId numbers at runtime; nothing reads the values back, only resets
const expanded = ref<string[]>([]);

const store = useMonthlyTrackingStore();
const { canAccessCareer } = useAccessibleCareers();
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25, stack: false });
const { mdAndUp } = useMobile();

// Drop any persisted career selection the user can no longer read.
store.careerIn = store.careerIn.filter(canAccessCareer);

const searchModel = computed({
  get: () => store.searchTerm,
  set: (v: string | null) => { store.searchTerm = v ?? ''; },
});

watch([selectedYear, selectedMonth], () => {
  expanded.value = [];
});

const { groupIds: groupFilterIds } = storeToRefs(useGroupFilterStore());
const {
  categoryIds: serviceCategoryFilterIds,
  uncategorized,
} = storeToRefs(useServiceCategoryFilterStore());

const { data, isPending, isFetching } = useQuery({
  queryKey: computed(() => [
    'monthlyTracking', selectedYear.value, selectedMonth.value, groupFilterIds.value,
    serviceCategoryFilterIds.value, uncategorized.value,
  ]),
  queryFn: () => monthlyTrackingApi.index(
    selectedYear.value, selectedMonth.value, groupFilterIds.value,
    { serviceCategoryIdIn: serviceCategoryFilterIds.value, uncategorized: uncategorized.value },
  ),
});

const rows = computed<MonthlyTrackingRow[]>(() => data.value?.monthlyTrackingRows || []);

const filteredRows = computed(() => {
  const afterSearch = applySearch(rows.value, store.searchTerm);
  const afterStatus = applyStatus(afterSearch, store.statusFilter);
  return applyCareer(afterStatus, store.careerIn);
});

const groupedRows = computed<GroupedPatientRow[]>(() => groupByPatient(filteredRows.value));

const availableCareers = computed<CareerOption[]>(() => {
  const seen = new Map<string, string>();
  rows.value.forEach((row) => {
    if (!seen.has(row.career)) seen.set(row.career, row.humanCareer);
  });
  return Array.from(seen.entries())
    .filter(([value]) => canAccessCareer(value))
    .sort(([, a], [, b]) => a.localeCompare(b))
    .map(([value, title]) => {
      const meta = careerMetadata(value);
      return { value, title, icon: meta.icon, color: meta.color };
    });
});

const kpiTotalPatients = computed(() => new Set(rows.value.map((r) => r.patientId)).size);
const kpiTotalCap = computed(() => rows.value.reduce((sum, r) => sum + r.cap, 0));
const kpiTotalPlanned = computed(() => rows.value.reduce((sum, r) => sum + r.planned, 0));
const kpiTotalActual = computed(() => rows.value.reduce((sum, r) => sum + r.actual, 0));

const globalPercent = computed(() => {
  if (kpiTotalPlanned.value === 0) return 0;
  return Math.round((kpiTotalActual.value / kpiTotalPlanned.value) * PERCENT_MULTIPLIER);
});

const headers = [
  { title: 'Paciente', key: 'patientFullName', sortable: true },
  { title: 'Presupuestados', key: 'cap', align: 'end' as const, sortable: true },
  { title: 'Agendados', key: 'planned', align: 'end' as const, sortable: true },
  { title: 'Realizados', key: 'actual', align: 'end' as const, sortable: true },
  { title: 'Utilización', key: 'percentUsed', align: 'end' as const, sortable: true },
  { title: 'Días por revisar', key: 'driftCount', align: 'center' as const, sortable: true },
];

const defaultSort = [
  { key: 'driftCount', order: 'desc' as const },
  { key: 'percentUsed', order: 'desc' as const },
];

function utilizationColor(percent: number): string {
  if (percent > OVER_THRESHOLD) return 'error';
  if (percent >= WARNING_THRESHOLD) return 'warning';
  if (percent > 0) return 'success';
  return 'grey';
}

function driftColor(count: number): string {
  if (count === 0) return 'success';
  if (count < HIGH_DRIFT) return 'warning';
  return 'error';
}

function formatPercent(p: number): string {
  return `${Math.round(p * PERCENT_MULTIPLIER)}%`;
}

const selectedMonthLabel = computed(() => {
  const name = MONTH_NAMES_ES[selectedMonth.value - 1] ?? '';
  const titled = name.charAt(0).toUpperCase() + name.slice(1);
  return `${titled} ${selectedYear.value}`;
});

const filtersActive = computed(() => (
  store.statusFilter !== 'all' ||
  store.careerIn.length > 0 ||
  store.searchTerm.trim() !== ''
));

function clearFilters() {
  store.statusFilter = 'all';
  store.careerIn = [];
  store.searchTerm = '';
}
</script>

<template>
  <v-container class="monthly-tracking-container">
    <!-- Page header -->
    <div class="d-flex align-center justify-space-between flex-wrap ga-3 mb-4">
      <div>
        <div class="d-flex align-center ga-2">
          <v-icon
            icon="mdi-chart-timeline-variant"
            color="primary"
            size="28"
          />
          <span class="text-h5 font-weight-bold">Seguimiento mensual</span>
        </div>
        <p class="text-body-2 text-medium-emphasis mt-1 mb-0">
          Compara los turnos presupuestados con los agendados y los realizados
          para cada paciente y especialidad.
        </p>
      </div>
      <div class="d-flex align-center ga-2">
        <v-select
          v-model="selectedMonth"
          :items="MONTH_OPTIONS"
          item-title="title"
          item-value="value"
          label="Mes"
          density="comfortable"
          variant="outlined"
          hide-details
          prepend-inner-icon="mdi-calendar-month"
          class="month-select"
        />
        <v-select
          v-model="selectedYear"
          :items="YEAR_OPTIONS"
          label="Año"
          density="comfortable"
          variant="outlined"
          hide-details
          class="year-select"
        />
      </div>
    </div>

    <!-- KPI cards -->
    <v-row
      dense
      class="mb-4"
    >
      <v-col
        cols="6"
        md="3"
      >
        <v-card
          variant="tonal"
          color="primary"
          rounded="lg"
        >
          <v-card-text class="py-4">
            <div class="d-flex align-center ga-2 text-caption text-medium-emphasis mb-1">
              <v-icon
                icon="mdi-account-multiple"
                size="small"
              />
              Pacientes
            </div>
            <div class="text-h4 font-weight-bold">
              {{ kpiTotalPatients }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="6"
        md="3"
      >
        <v-card
          variant="tonal"
          color="info"
          rounded="lg"
        >
          <v-card-text class="py-4">
            <div class="d-flex align-center ga-2 text-caption text-medium-emphasis mb-1">
              <v-icon
                icon="mdi-target"
                size="small"
              />
              Turnos presupuestados
            </div>
            <div class="text-h4 font-weight-bold">
              {{ kpiTotalCap }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="6"
        md="3"
      >
        <v-card
          variant="tonal"
          color="warning"
          rounded="lg"
        >
          <v-card-text class="py-4">
            <div class="d-flex align-center ga-2 text-caption text-medium-emphasis mb-1">
              <v-icon
                icon="mdi-calendar-check"
                size="small"
              />
              Turnos agendados
            </div>
            <div class="text-h4 font-weight-bold">
              {{ kpiTotalPlanned }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="6"
        md="3"
      >
        <v-card
          variant="tonal"
          color="success"
          rounded="lg"
        >
          <v-card-text class="py-4">
            <div class="d-flex align-center ga-2 text-caption text-medium-emphasis mb-1">
              <v-icon
                icon="mdi-check-circle"
                size="small"
              />
              Turnos realizados
            </div>
            <div class="d-flex align-end ga-2">
              <div class="text-h4 font-weight-bold">
                {{ kpiTotalActual }}
              </div>
              <div class="text-caption text-medium-emphasis pb-1">
                ({{ globalPercent }}%)
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Table card -->
    <v-card
      rounded="lg"
      elevation="1"
    >
      <v-card-title class="d-flex align-center justify-space-between pa-4 pb-2 flex-wrap ga-2">
        <div class="d-flex align-center ga-2">
          <v-icon
            icon="mdi-table"
            color="primary"
          />
          <span class="text-subtitle-1 font-weight-bold">{{ selectedMonthLabel }}</span>
          <v-chip
            size="small"
            variant="tonal"
          >
            {{ groupedRows.length }} {{ groupedRows.length === 1 ? 'paciente' : 'pacientes' }}
          </v-chip>
        </div>
        <div class="d-flex align-center ga-2 flex-wrap">
          <v-text-field
            v-model="searchModel"
            placeholder="Buscar por paciente"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            class="search-field"
          />
          <group-filter-select
            v-model="groupFilterIds"
            variant="outlined"
            density="compact"
            hide-details
            class="tracking-group-filter"
          />
          <service-category-filter-select
            v-model="serviceCategoryFilterIds"
            v-model:uncategorized="uncategorized"
            variant="outlined"
            density="compact"
            hide-details
            class="tracking-group-filter"
          />
        </div>
      </v-card-title>

      <!-- Filters row -->
      <div class="d-flex align-center flex-wrap ga-3 px-4 pb-3">
        <v-chip-group
          v-model="store.statusFilter"
          mandatory
          selected-class="bg-primary text-on-primary"
          class="status-chip-group"
        >
          <v-chip
            v-for="option in STATUS_OPTIONS"
            :key="option.value"
            :value="option.value"
            :prepend-icon="option.icon"
            size="small"
            variant="tonal"
            filter
          >
            {{ option.title }}
          </v-chip>
        </v-chip-group>

        <v-select
          v-model="store.careerIn"
          :items="availableCareers"
          item-title="title"
          item-value="value"
          label="Especialidades"
          variant="outlined"
          density="compact"
          hide-details
          multiple
          chips
          closable-chips
          clearable
          prepend-inner-icon="mdi-stethoscope"
          class="career-select"
        >
          <template #chip="{ item, props: chipProps }">
            <v-chip
              v-bind="chipProps"
              :color="careerMetadata(item.raw.value).color"
              :prepend-icon="careerMetadata(item.raw.value).icon"
              size="small"
            >
              {{ item.raw.title }}
            </v-chip>
          </template>
        </v-select>

        <v-btn
          v-if="filtersActive"
          variant="text"
          size="small"
          prepend-icon="mdi-close"
          @click="clearFilters"
        >
          Limpiar filtros
        </v-btn>
      </div>

      <v-divider />

      <v-data-table
        v-if="mdAndUp"
        v-bind="tableProps"
        v-model:expanded="expanded"
        :headers="headers"
        :items="groupedRows"
        :loading="isPending || isFetching"
        :sort-by="defaultSort"
        item-value="patientId"
        density="comfortable"
        loading-text="Cargando seguimiento..."
        :no-data-text="filtersActive ?
          'Ningún paciente coincide con los filtros aplicados' :
          'No hay datos para este mes'"
        show-expand
        hover
      >
        <template #[`item.patientFullName`]="{ item }">
          <div class="d-flex align-center ga-2">
            <a
              v-if="canOpenPatient"
              :href="patientRequirementsPath(item.patientId)"
              class="patient-name-link font-weight-medium"
            >{{ item.patientFullName || '—' }}</a>
            <span
              v-else
              class="font-weight-medium"
            >{{ item.patientFullName || '—' }}</span>
            <v-chip
              size="x-small"
              variant="tonal"
              color="primary"
            >
              {{ item.careerCount }} esp.
            </v-chip>
          </div>
        </template>

        <template #[`item.percentUsed`]="{ item }">
          <span
            class="text-body-2 font-weight-medium"
            :class="`text-${utilizationColor(item.percentUsed)}`"
          >
            {{ formatPercent(item.percentUsed) }}
          </span>
        </template>

        <template #[`item.driftCount`]="{ item }">
          <v-chip
            size="small"
            :color="driftColor(item.driftCount)"
            variant="tonal"
            :prepend-icon="item.driftCount === 0 ? 'mdi-check' : 'mdi-alert-circle-outline'"
          >
            {{ item.driftCount === 0 ? 'Sin novedad' : `${item.driftCount} día(s)` }}
            <v-tooltip
              v-if="item.driftCount > 0"
              activator="parent"
              location="top"
            >
              <div class="drift-tooltip">
                <div class="font-weight-medium mb-1">
                  Días por revisar:
                </div>
                <div
                  v-for="day in item.driftDays"
                  :key="day"
                >
                  · {{ day }}
                </div>
              </div>
            </v-tooltip>
          </v-chip>
        </template>

        <template #expanded-row="{ item }">
          <tr
            v-for="child in item.children"
            :key="`${item.patientId}-${child.career}`"
            class="child-row"
          >
            <td>
              <div class="d-flex align-center ga-2">
                <v-avatar
                  :color="careerMetadata(child.career).color"
                  size="28"
                  variant="tonal"
                >
                  <v-icon
                    :icon="careerMetadata(child.career).icon"
                    size="16"
                  />
                </v-avatar>
                <span class="text-body-2">{{ child.humanCareer }}</span>
              </div>
            </td>
            <td class="text-end">
              {{ child.cap }}
            </td>
            <td class="text-end">
              {{ child.planned }}
            </td>
            <td class="text-end">
              {{ child.actual }}
            </td>
            <td class="text-end">
              <span
                class="text-body-2 font-weight-medium"
                :class="`text-${utilizationColor(child.percentUsed)}`"
              >
                {{ formatPercent(child.percentUsed) }}
              </span>
            </td>
            <td class="text-center">
              <v-chip
                size="small"
                :color="driftColor(child.driftDays.length)"
                variant="tonal"
                :prepend-icon="child.driftDays.length === 0 ?
                  'mdi-check' :
                  'mdi-alert-circle-outline'"
              >
                {{ child.driftDays.length === 0 ?
                  'Sin novedad' :
                  `${child.driftDays.length} día(s)` }}
              </v-chip>
            </td>
            <td />
          </tr>
        </template>
      </v-data-table>

      <!-- Mobile: card list -->
      <div
        v-else
        class="d-flex flex-column ga-2 pa-2"
      >
        <monthly-tracking-card
          v-for="row in groupedRows"
          :key="row.patientId"
          :row="row"
          :link-to-patient="canOpenPatient"
        />
        <p
          v-if="!groupedRows.length"
          class="text-center text-medium-emphasis py-6"
        >
          {{ (isPending || isFetching)
            ? 'Cargando seguimiento...'
            : (filtersActive ? 'Ningún paciente coincide con los filtros aplicados' : 'No hay datos para este mes') }}
        </p>
      </div>
    </v-card>
  </v-container>
</template>

<style scoped>
/* Clickable, but it keeps looking like the plain name it replaced. */
.patient-name-link {
  color: inherit;
  text-decoration: none;
}

.monthly-tracking-container {
  max-width: 1400px;
}

.month-select {
  min-width: 160px;
  max-width: 180px;
}

.year-select {
  min-width: 120px;
  max-width: 140px;
}

.tracking-group-filter {
  min-width: 220px;
  max-width: 260px;
}

.search-field {
  min-width: 280px;
  max-width: 320px;
}

.career-select {
  min-width: 280px;
  max-width: 360px;
}

/* below Vuetify's md breakpoint the fixed widths force horizontal overflow,
   so filters become full-width stacked rows instead */
@media (max-width: 959px) {
  .month-select,
  .year-select,
  .tracking-group-filter,
  .search-field,
  .career-select {
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }
}

.status-chip-group {
  flex: 0 0 auto;
}

.drift-tooltip {
  max-width: 220px;
  font-size: 12px;
}

.child-row {
  background-color: rgba(var(--v-theme-primary), .02);
}
</style>
